// src/components/family/profile/MedicationSummaryCard.tsx
// Current medications summary card for child profile overview

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Pill,
  ChevronRight,
  Clock,
  AlertTriangle,
  Bell,
  Plus,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import type { Medication } from '@/types';

interface MedicationSummaryCardProps {
  medications: Medication[];
  childId: string;
  isLoading?: boolean;
  className?: string;
}

const MedicationItem: React.FC<{ medication: Medication }> = ({ medication }) => {
  // Parse reminder times if any
  const hasReminders = medication.reminderEnabled && medication.reminderTimes?.length > 0;

  return (
    <div className="flex items-center gap-3 py-3 px-4 -mx-4 hover:bg-cream/50 transition-colors group cursor-pointer">
      {/* Pill icon with color based on medication type/name */}
      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
        <Pill className="w-5 h-5 text-blue-500" />
      </div>

      {/* Medication details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-navy truncate group-hover:text-sage-dark transition-colors">
            {medication.name}
          </h4>
          {hasReminders && (
            <Bell className="w-3.5 h-3.5 text-sage flex-shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-sm text-warm-gray">{medication.dosage}</span>
          <span className="text-warm-gray/50">•</span>
          <span className="text-sm text-warm-gray">{medication.frequency}</span>
        </div>
      </div>

      {/* Status indicator */}
      <div className="flex-shrink-0">
        {medication.isActive ? (
          <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Active
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-medium text-warm-gray">
            <XCircle className="w-3.5 h-3.5" />
            Inactive
          </span>
        )}
      </div>
    </div>
  );
};

export const MedicationSummaryCard: React.FC<MedicationSummaryCardProps> = ({
  medications,
  childId,
  isLoading = false,
  className = '',
}) => {
  if (isLoading) {
    return <MedicationSummaryCardSkeleton />;
  }

  // Filter to active medications only
  const activeMedications = medications.filter((med) => med.isActive);
  
  // Check for any medications that might need attention
  const hasPRNMedications = medications.some((med) => 
    med.frequency?.toLowerCase().includes('prn') || 
    med.frequency?.toLowerCase().includes('as needed')
  );

  return (
    <div className={`bg-white rounded-2xl border border-cream-dark ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
        <h3 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
          <Pill className="w-5 h-5 text-blue-500" />
          Medications
          {activeMedications.length > 0 && (
            <span className="text-sm font-normal text-warm-gray">
              ({activeMedications.length})
            </span>
          )}
        </h3>
        <Link
          to={`/family/children/${childId}/medications`}
          className="text-sm text-sage font-medium hover:text-sage-dark flex items-center gap-1 transition-colors"
        >
          Manage
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Alert banner for PRN medications */}
      {hasPRNMedications && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Some medications are PRN (as needed)</span>
        </div>
      )}

      {/* Medications list */}
      <div className="px-4 py-2 divide-y divide-cream-dark/50">
        {activeMedications.length > 0 ? (
          activeMedications.slice(0, 4).map((medication) => (
            <MedicationItem key={medication.id} medication={medication} />
          ))
        ) : (
          <EmptyMedicationsState childId={childId} />
        )}
      </div>

      {/* View more link */}
      {activeMedications.length > 4 && (
        <div className="px-4 py-2 border-t border-cream-dark">
          <Link
            to={`/family/children/${childId}/medications`}
            className="text-sm text-sage font-medium hover:text-sage-dark transition-colors"
          >
            View {activeMedications.length - 4} more medication
            {activeMedications.length - 4 !== 1 ? 's' : ''}
          </Link>
        </div>
      )}

      {/* Footer quick actions */}
      {activeMedications.length > 0 && (
        <div className="px-4 py-3 border-t border-cream-dark bg-cream/30">
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/family/children/${childId}/medications/new`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-cream-dark rounded-lg text-xs font-medium text-navy hover:border-sage/30 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Medication
            </Link>
            <Link
              to={`/family/children/${childId}/medications/log`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 transition-colors"
            >
              <Clock className="w-3.5 h-3.5" />
              Log Dose
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Empty state
const EmptyMedicationsState: React.FC<{ childId: string }> = ({ childId }) => (
  <div className="py-6 text-center">
    <div className="w-12 h-12 rounded-full bg-cream-dark flex items-center justify-center mx-auto mb-3">
      <Pill className="w-6 h-6 text-warm-gray" />
    </div>
    <p className="text-sm text-warm-gray mb-3">No medications recorded</p>
    <Link
      to={`/family/children/${childId}/medications/new`}
      className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium text-sm hover:bg-sage-dark transition-colors"
    >
      <Plus className="w-4 h-4" />
      Add Medication
    </Link>
  </div>
);

// Loading skeleton
export const MedicationSummaryCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl border border-cream-dark animate-pulse">
    <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-cream-dark rounded" />
        <div className="h-6 bg-cream-dark rounded w-28" />
      </div>
      <div className="h-4 bg-cream-dark rounded w-16" />
    </div>

    <div className="px-4 py-2 space-y-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <div className="w-10 h-10 rounded-xl bg-cream-dark" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 bg-cream-dark rounded w-32" />
            <div className="h-3 bg-cream-dark rounded w-48" />
          </div>
          <div className="h-4 bg-cream-dark rounded w-14" />
        </div>
      ))}
    </div>
  </div>
);

export default MedicationSummaryCard;
